import { useEffect, useRef, useState } from "react"
import { Plus, ChevronDown, ChevronUp, X, Check } from "lucide-react"
import { Button, Input, NumberInput, Select, Currency, StatusBadge, DataTable } from "../../ui"
import {
  useGetVendorProposalsQuery,
  useGetGroupVendorProposalsQuery,
  useCreateVendorProposalRequestMutation,
  useGetPurchaseVendorProposalsQuery,
  useCreatePurchaseVendorProposalRequestMutation,
  useGetPurchaseQuery,
  useSwitchPurchaseToInternalStockMutation,
} from "shared/redux/endpoints/procurementPurchases"
import { useGetOrderReviewQuery } from "shared/redux/endpoints/procurementApprovals"
import { useLazyGetProcurementSupplierItemPricesQuery } from "shared/redux/endpoints/procurementItems"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { warnMissingInviteEmails } from "../../actionResultFeedback"
import {
  NO_QUOTE_LABEL,
  quotedItems,
  hasQuote,
  proposalTotal,
  formatVND,
  PRICE_SOURCE_LABEL,
  PRICE_DEVIATION_WARN_VND,
  proposalLineKey,
  proposalLineRef,
  formatWarranty,
  isValidWarrantyMonths,
  isInternalStockSupplier,
} from "../../constants"
import CreateProposalDrawer from "./CreateProposalDrawer"
import InternalStockPicker from "./InternalStockPicker"
import { RULES, validateFields, fieldError, hasErrors } from "../../fieldRules"
import VendorComparisonModal from "./VendorComparisonModal"
import LinkActions, { proposalLink } from "./LinkActions"
import { requirementNames } from "./proposalScoring"
import RequirementsSection from "./RequirementsSection"

// This panel (and everything in this file) works against two different
// "sources" of vendor proposals — an Order Request or a Purchase — which
// hit distinct BE endpoints (see procurementPurchases.js: getVendorProposals/
// createVendorProposalRequest for orders, getPurchaseVendorProposals/
// createPurchaseVendorProposalRequest for purchases). `sourceType` picks
// which one a given usage means; both mutation/query hooks are always called
// (never conditionally — Rules of Hooks) and the *result* is selected based
// on `sourceType` instead.
const SOURCE_TYPE = { ORDER: "order", GROUP: "group", PURCHASE: "purchase" }

// Builds the create-mutation's arguments for whichever source is active —
// the two endpoints take the same body shape but a different id field
// (`orderRequestId` in the body for orders; `id`, consumed into the URL
// path, for purchases).
// Group Orders use the order endpoint with `groupOrderId` in the body.
const buildCreateArgs = (sourceType, sourceId, payload) => {
  if (sourceType === SOURCE_TYPE.PURCHASE) return { id: sourceId, ...payload }
  if (sourceType === SOURCE_TYPE.GROUP) return { groupOrderId: sourceId, ...payload }
  return { orderRequestId: sourceId, ...payload }
}

const isExpired = (proposal) => !!proposal.expiredAt && new Date(proposal.expiredAt) < new Date()

const formatDeadline = (iso) => {
  if (!iso) return "—"
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// "Còn N ngày" / "Còn N giờ" / "Quá hạn" — computed client-side from expiredAt,
// same idea as the old mock's countdown.
const deadlineRemaining = (iso) => {
  if (!iso) return null
  const diff = new Date(iso) - Date.now()
  if (diff <= 0) return { text: "Quá hạn", expired: true }
  const hours = Math.floor(diff / 3600000)
  if (hours < 24) return { text: `Còn ${hours} giờ`, expired: false }
  return { text: `Còn ${Math.ceil(hours / 24)} ngày`, expired: false }
}

// Table of vendor proposals for this order — one row per supplier, Supplier /
// Items / Total / Status / Link, expandable to the per-item breakdown.
function ProposalsTable({ proposals, isLoading }) {
  const [expandedKeys, setExpandedKeys] = useState(new Set())

  const toggleExpand = (row) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(row.id)) next.delete(row.id)
      else next.add(row.id)
      return next
    })

  const columns = [
    {
      key: "supplierName",
      header: "Supplier",
      render: (row) => (
        <div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <strong>{row.supplierName || row.supplier?.name}</strong>
            {row.internalStock && <StatusBadge status="info" label="Internal stock" dot={false} />}
            {row.isSelected && <StatusBadge status="approved" label="Selected" />}
          </span>
          <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>{row.supplier?.email || "—"}</div>
          {row.proposalTaxCode && (
            <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>MST báo giá: {row.proposalTaxCode}</div>
          )}
        </div>
      ),
    },
    {
      key: "itemCount",
      header: "Items",
      align: "right",
      value: (row) => quotedItems(row).length,
      render: (row) => quotedItems(row).length,
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      value: (row) => proposalTotal(row),
      render: (row) => (
        <div>
          {hasQuote(row) ? <Currency value={proposalTotal(row)} /> : <span className="pc-muted">{NO_QUOTE_LABEL}</span>}
          {row.deliveryFee != null && (
            <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>+ phí giao {formatVND(row.deliveryFee)}</div>
          )}
        </div>
      ),
    },
    {
      key: "deadline",
      header: "Deadline",
      render: (row) => {
        const remaining = deadlineRemaining(row.expiredAt)
        return (
          <div>
            <div>{formatDeadline(row.expiredAt)}</div>
            {remaining && (
              <div style={{ fontSize: 12, color: remaining.expired ? "var(--pc-danger)" : "var(--pc-muted)" }}>
                {remaining.text}
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        if (hasQuote(row)) return <StatusBadge status="approved" label="Quoted" />
        if (isExpired(row)) return <StatusBadge status="rejected" label="Expired" />
        return <StatusBadge status="pending" label={NO_QUOTE_LABEL} />
      },
    },
    {
      key: "link",
      header: "Link",
      // Internal-stock proposals have no vendor link (public link answers 403).
      render: (row) => (row.internalStock ? "—" : <LinkActions link={proposalLink(row)} />),
    },
  ]

  const rows = (proposals || []).map((p) => ({ ...p, expandable: hasQuote(p) }))

  return (
    <DataTable
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      hidePagination
      expandedKeys={expandedKeys}
      onToggleExpand={toggleExpand}
      renderExpanded={(row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
          {quotedItems(row).map((it) => (
            <div
              key={it.id ?? proposalLineKey(it)}
              style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--pc-body)" }}
            >
              <span>
                {it.itemName}
                {it.unitPrice != null ? ` · ${formatVND(it.unitPrice)}/đv` : ""}
                {it.note ? ` — ${it.note}` : ""}
                {formatWarranty(it) ? ` · ${formatWarranty(it)}` : ""}
                {PRICE_SOURCE_LABEL[it.priceSource] ? ` · ${PRICE_SOURCE_LABEL[it.priceSource]}` : ""}
              </span>
              <Currency value={it.proposedAmount} />
            </div>
          ))}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              fontWeight: 700,
              borderTop: "1px solid var(--pc-border)",
              paddingTop: 4,
            }}
          >
            <span>Total ({quotedItems(row).length} item(s))</span>
            <Currency value={proposalTotal(row)} />
          </div>
        </div>
      )}
    />
  )
}

let blockSeq = 0
const makeBlock = () => ({
  key: `blk-${(blockSeq += 1)}`,
  supplierId: "",
  amounts: {}, // proposalLineKey -> { proposedAmount, note, auto, autoNote }
  catalogue: {}, // itemId -> catalogue row from item-prices (pre-fill hints)
  loadingPrices: false,
  showNewSupplier: false,
  newSupplierDraft: { name: "", taxCode: "", email: "" },
})

// Staff-side "quote on behalf of a supplier" form — one row per order line,
// only lines with an amount > 0 are sent. Table layout mirrors the public
// vendor-proposal page's own quote table (item name + muted item code,
// quantity, unit, one lump-sum amount per line, note) so filling this in
// looks the same whether it's the supplier doing it or staff doing it for
// them over the phone.
//
// Supports quoting several suppliers in one go: each "block" below is its
// own supplier + price table, "+ Add another supplier" appends a blank one,
// and each block's Supplier dropdown excludes whichever supplier every OTHER
// block already picked. Uses the same `createVendorProposalRequest` endpoint
// as CreateProposalDrawer, but `title`/`terms`/`expiredAt` are auto-filled
// placeholders (not user-facing fields here — this form is meant to stay
// quick) and `pinEnabled: false` since no link ever goes out. Each block is
// its own call (Promise.allSettled) — `items[].proposedAmount` applies to
// every supplier in one call's `supplierIds`, so two *differently priced*
// suppliers can never share a call.
function NewProposalForm({
  sourceType,
  sourceId,
  resolveSourceId = async () => sourceId,
  orderLines,
  orderProposals,
  currentTerms,
  prefill,
  itemsLoading,
  supplierOptions,
  onCreateSupplier,
  creatingSupplier,
  autoOpen,
  flushRef,
}) {
  const [expanded, setExpanded] = useState(autoOpen)

  // Soft sanity check on a staff-typed price: returns the nearest order-level
  // quote for the same item when the typed price is more than the threshold
  // away from EVERY such quote (any supplier), else null.
  const deviatingReference = (line, amount) => {
    const typed = Number(amount)
    if (!(typed > 0)) return null
    const refs = (orderProposals || []).flatMap((p) =>
      quotedItems(p)
        .filter((it) => proposalLineKey(it) === line.key)
        .map((it) => Number(it.proposedAmount))
    )
    if (refs.length === 0) return null
    const nearest = refs.reduce((a, b) => (Math.abs(b - typed) < Math.abs(a - typed) ? b : a))
    return Math.abs(typed - nearest) > PRICE_DEVIATION_WARN_VND ? nearest : null
  }
  const [createOrderProposal] = useCreateVendorProposalRequestMutation()
  const [createPurchaseProposal] = useCreatePurchaseVendorProposalRequestMutation()
  const createVendorProposalRequest =
    sourceType === SOURCE_TYPE.PURCHASE ? createPurchaseProposal : createOrderProposal
  const [blocks, setBlocks] = useState(() => [makeBlock()])
  const [saving, setSaving] = useState(false)

  // "Use these prices" on a linked-order proposal: drop its supplier + item
  // prices into a block here (reusing the block already on that supplier, or
  // an empty one, else a new one). Only items that are lines of this PO fill.
  useEffect(() => {
    if (!prefill?.proposal) return
    const { supplierId, items } = prefill.proposal
    const amounts = {}
    quotedItems({ items }).forEach((it) => {
      if (!orderLines.some((l) => l.key === proposalLineKey(it))) return
      amounts[proposalLineKey(it)] = {
        proposedAmount: String(it.proposedAmount),
        note: it.note || "",
        warrantyMonths: it.warrantyMonths != null ? String(it.warrantyMonths) : "",
        warrantyNote: it.warrantyNote || "",
        auto: false,
      }
    })
    setExpanded(true)
    setBlocks((prev) => {
      const sameSupplier = prev.findIndex((b) => String(b.supplierId) === String(supplierId))
      const empty = prev.findIndex((b) => !b.supplierId && Object.keys(b.amounts).length === 0)
      const idx = sameSupplier >= 0 ? sameSupplier : empty
      if (idx >= 0) return prev.map((b, i) => (i === idx ? { ...b, supplierId, amounts } : b))
      return [...prev, { ...makeBlock(), supplierId, amounts }]
    })
  }, [prefill?.seq])

  // No PO yet → `resolveSourceId("prices")` creates the draft and navigates to
  // its edit page (which mounts this form already expanded via `autoOpen`),
  // so nothing is typed on a page that is about to be replaced.
  const handleToggleExpand = async () => {
    if (!expanded && !sourceId) {
      await resolveSourceId("prices")
      return
    }
    setExpanded((v) => !v)
  }

  const updateBlock = (key, patch) =>
    setBlocks((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)))
  const updateBlockAmount = (key, lineKey, patch) =>
    setBlocks((prev) =>
      prev.map((b) =>
        b.key === key ? { ...b, amounts: { ...b.amounts, [lineKey]: { ...b.amounts[lineKey], ...patch } } } : b
      )
    )
  const removeBlock = (key) => setBlocks((prev) => prev.filter((b) => b.key !== key))

  // Catalogue pre-fill. `latestSupplier` guards against a slow response for a
  // previously picked supplier landing after the user switched again.
  const [fetchItemPrices] = useLazyGetProcurementSupplierItemPricesQuery()
  const latestSupplier = useRef({})
  const canPrefill = !!sourceId
  const itemPricesScope =
    sourceType === SOURCE_TYPE.PURCHASE
      ? { purchaseId: sourceId }
      : sourceType === SOURCE_TYPE.GROUP
      ? { groupOrderId: sourceId }
      : { orderRequestId: sourceId }

  // overwrite=false only fills empty/0 inputs (or ones this pre-fill filled
  // earlier and the user hasn't touched); overwrite=true is the explicit reload.
  const loadCataloguePrices = async (key, supplierId, overwrite = false) => {
    latestSupplier.current[key] = supplierId
    if (!supplierId || !canPrefill) return
    updateBlock(key, { loadingPrices: true })
    try {
      const rows = await fetchItemPrices({ supplierId, ...itemPricesScope }, false).unwrap()
      if (latestSupplier.current[key] !== supplierId) return
      const byItem = {}
      ;(rows || []).forEach((r) => {
        byItem[String(r.itemId)] = r
      })
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.key !== key || String(b.supplierId) !== String(supplierId)) return b
          const amounts = { ...b.amounts }
          orderLines.forEach((l) => {
            const r = l.itemId != null ? byItem[String(l.itemId)] : undefined
            if (!r?.priceFound) return
            const cur = amounts[l.key] || {}
            const next = { ...cur }
            if (overwrite || cur.auto || !(Number(cur.proposedAmount) > 0)) {
              next.proposedAmount = String(r.totalPrice)
              next.auto = true
            }
            if (r.note && (overwrite || cur.autoNote || !cur.note)) {
              next.note = r.note
              next.autoNote = true
            }
            amounts[l.key] = next
          })
          return { ...b, amounts, catalogue: byItem, loadingPrices: false }
        })
      )
    } catch (e) {
      if (latestSupplier.current[key] !== supplierId) return
      updateBlock(key, { loadingPrices: false, catalogue: {} })
      showToastError(getApiErrorMessage(e, "Không thể tải giá catalogue. Bạn vẫn có thể nhập giá thủ công."))
    }
  }

  const handleSupplierChange = (key, supplierId) => {
    updateBlock(key, { supplierId, catalogue: {}, loadingPrices: false })
    loadCataloguePrices(key, supplierId)
  }

  const quotedItemsFor = (block) => orderLines.filter((l) => Number(block.amounts[l.key]?.proposedAmount) > 0)
  const validBlocks = blocks.filter((b) => b.supplierId && quotedItemsFor(b).length > 0)
  const warrantyValid = blocks.every((b) => Object.values(b.amounts).every((a) => isValidWarrantyMonths(a?.warrantyMonths)))
  const canSave = validBlocks.length > 0 && warrantyValid && !saving

  const handleSave = async () => {
    if (!canSave) return false
    setSaving(true)
    try {
      const id = await resolveSourceId()
      if (!id) return false
      const expiredAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      const results = await Promise.allSettled(
        validBlocks.map((b) => {
          const supplierName = supplierOptions.find((o) => String(o.value) === String(b.supplierId))?.label || ""
          return createVendorProposalRequest(
            buildCreateArgs(sourceType, id, {
              supplierIds: [b.supplierId],
              title: `Báo giá trực tiếp${supplierName ? ` - ${supplierName}` : ""}`,
              // Resend the OR/PO's requirement set unchanged — a placeholder would replace it (or 409 once locked).
              terms: currentTerms.length ? currentTerms : ["Đã nhận báo giá trực tiếp từ nhà cung cấp"],
              pinEnabled: false,
              expiredAt,
              items: quotedItemsFor(b).map((l) => ({
                ...proposalLineRef(l),
                proposedAmount: Number(b.amounts[l.key].proposedAmount),
                note: b.amounts[l.key]?.note || undefined,
                warrantyMonths:
                  b.amounts[l.key]?.warrantyMonths === "" || b.amounts[l.key]?.warrantyMonths == null
                    ? undefined
                    : Number(b.amounts[l.key].warrantyMonths),
                warrantyNote: b.amounts[l.key]?.warrantyNote?.trim() || undefined,
              })),
            })
          ).unwrap()
        })
      )
      const okCount = results.filter((r) => r.status === "fulfilled").length
      const failCount = results.length - okCount
      if (okCount > 0) {
        showToastSuccess(`Đã lưu báo giá cho ${okCount} nhà cung cấp` + (failCount ? `, ${failCount} lỗi.` : "."))
        setBlocks([makeBlock()])
      } else {
        showToastError(getApiErrorMessage(results[0]?.reason, "Không thể lưu báo giá. Vui lòng thử lại."))
      }
      return failCount === 0
    } finally {
      setSaving(false)
    }
  }

  // Lets the parent form save any quote still sitting in this form (typed but
  // never "Save proposal"-ed) before it submits the order. true = nothing
  // pending or everything saved; false = a save failed, caller should stop.
  if (flushRef) flushRef.current = async () => (validBlocks.length === 0 ? true : handleSave())

  const handleCreateSupplierForBlock = async (key, draft) => {
    if (hasErrors(validateFields(draft, RULES.supplier))) {
      updateBlock(key, { showSupplierErrors: true })
      return
    }
    try {
      const created = await onCreateSupplier(draft)
      updateBlock(key, {
        supplierId: created.id,
        showNewSupplier: false,
        showSupplierErrors: false,
        newSupplierDraft: { name: "", taxCode: "", email: "" },
      })
    } catch {
      // onCreateSupplier already surfaces its own toast on failure.
    }
  }

  const buildColumns = (block) => [
    {
      key: "item",
      header: "Hạng mục",
      render: (l) => (
        <div>
          <div style={{ fontWeight: 600, color: "var(--pc-ink)" }}>{l.itemName}</div>
          {l.itemCode && <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>{l.itemCode}</div>}
        </div>
      ),
    },
    { key: "quantity", header: "SL", align: "right", value: (l) => l.quantity },
    { key: "uom", header: "Đơn vị", value: (l) => l.uom || "—" },
    {
      key: "proposedAmount",
      header: "Báo giá cho cả SL (VND)",
      render: (l) => {
        const cat = l.itemId != null ? block.catalogue[String(l.itemId)] : undefined
        const refPrice = deviatingReference(l, block.amounts[l.key]?.proposedAmount)
        return (
          <div>
            <NumberInput
              placeholder="0"
              value={block.amounts[l.key]?.proposedAmount || ""}
              onChange={(v) => updateBlockAmount(block.key, l.key, { proposedAmount: v, auto: false })}
            />
            {refPrice != null && (
              <div style={{ fontSize: 12, color: "var(--pc-warn)", marginTop: 2 }}>
                Giá chênh hơn {formatVND(PRICE_DEVIATION_WARN_VND)} so với báo giá từ order ({formatVND(refPrice)}).
                Bạn có chắc chắn giá này đúng không?
              </div>
            )}
            {cat?.priceFound && (
              <div style={{ fontSize: 12, color: "var(--pc-muted)", marginTop: 2 }}>
                Catalogue: {Number(cat.unitPrice).toLocaleString("vi-VN")} × {Number(cat.quantity).toLocaleString("vi-VN")}
              </div>
            )}
            {cat && !cat.priceFound && (
              <div style={{ fontSize: 12, color: "var(--pc-muted)", marginTop: 2 }}>
                {cat.message || "No catalogue price for this supplier"}
                {cat.vendorPriceStatus === "EXPIRED" && cat.vendorPrice != null
                  ? ` Giá cũ: ${formatVND(cat.vendorPrice)}.`
                  : ""}
                {Number(cat.standardPrice) > 0 && Number(cat.quantity) > 0 && (
                  <button
                    type="button"
                    className="pc-btn pc-btn--ghost pc-btn--sm"
                    onClick={() =>
                      updateBlockAmount(block.key, l.key, {
                        proposedAmount: String(Math.round(Number(cat.standardPrice) * Number(cat.quantity))),
                        auto: false,
                      })
                    }
                  >
                    Dùng giá nguyên tắc {formatVND(cat.standardPrice)}
                  </button>
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: "note",
      header: "Ghi chú",
      render: (l) => (
        <Input
          placeholder="Model / ghi chú"
          value={block.amounts[l.key]?.note || ""}
          onChange={(e) => updateBlockAmount(block.key, l.key, { note: e.target.value, autoNote: false })}
        />
      ),
    },
    {
      key: "warranty",
      header: "Bảo hành",
      render: (l) => {
        const months = block.amounts[l.key]?.warrantyMonths ?? ""
        const invalid = !isValidWarrantyMonths(months)
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Input
                type="number"
                min={0}
                step={1}
                placeholder="0"
                aria-label="Số tháng bảo hành"
                value={months}
                onChange={(e) => updateBlockAmount(block.key, l.key, { warrantyMonths: e.target.value })}
                style={{ width: 72 }}
              />
              <span style={{ fontSize: 12, color: "var(--pc-muted)" }}>tháng</span>
            </div>
            {invalid && <div style={{ fontSize: 12, color: "var(--pc-danger, #b42318)" }}>Số nguyên ≥ 0.</div>}
            <Input
              placeholder="Điều kiện bảo hành"
              value={block.amounts[l.key]?.warrantyNote || ""}
              onChange={(e) => updateBlockAmount(block.key, l.key, { warrantyNote: e.target.value })}
            />
          </div>
        )
      },
    },
  ]

  return (
    <div
      style={{
        border: "1px solid var(--pc-border)",
        background: "var(--pc-surface-2)",
        borderRadius: "var(--pc-r-md)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleToggleExpand}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleToggleExpand()
        }}
        style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, cursor: "pointer" }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Or enter prices yourself</div>
          <div style={{ fontSize: 13, color: "var(--pc-body)" }}>
            Already have the numbers (e.g. from a call)? Enter them directly instead of sending a link.
          </div>
        </div>
        {expanded ? <ChevronUp size={18} aria-hidden /> : <ChevronDown size={18} aria-hidden />}
      </div>

      {expanded && (
        <>
          {blocks.map((block) => {
            const takenByOthers = new Set(
              blocks.filter((b) => b.key !== block.key && b.supplierId).map((b) => String(b.supplierId))
            )
            const optionsForBlock = supplierOptions.filter(
              (o) => !takenByOthers.has(String(o.value)) && !isInternalStockSupplier(o.value)
            )

            return (
              <div
                key={block.key}
                style={{
                  border: "1px dashed var(--pc-border-strong)",
                  borderRadius: "var(--pc-r-md)",
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <Select
                      label="Supplier"
                      value={block.supplierId}
                      onChange={(v) => handleSupplierChange(block.key, v)}
                      options={optionsForBlock}
                      placeholder="Select supplier…"
                    />
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {canPrefill && block.supplierId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={block.loadingPrices}
                        onClick={() => loadCataloguePrices(block.key, block.supplierId, true)}
                      >
                        Reload catalogue prices
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => updateBlock(block.key, { showNewSupplier: !block.showNewSupplier })}
                    >
                      + Create new supplier
                    </Button>
                    {blocks.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<X size={14} aria-hidden />}
                        onClick={() => removeBlock(block.key)}
                        aria-label="Remove this supplier"
                      />
                    )}
                  </div>
                </div>

                {block.showNewSupplier && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Input
                      label="Supplier Name"
                      required
                      value={block.newSupplierDraft.name}
                      error={block.showSupplierErrors ? fieldError(block.newSupplierDraft.name, RULES.supplier.name) : undefined}
                      onChange={(e) =>
                        updateBlock(block.key, { newSupplierDraft: { ...block.newSupplierDraft, name: e.target.value } })
                      }
                      disabled={creatingSupplier}
                    />
                    <Input
                      label="Tax Code"
                      required
                      value={block.newSupplierDraft.taxCode}
                      error={block.showSupplierErrors ? fieldError(block.newSupplierDraft.taxCode, RULES.supplier.taxCode) : undefined}
                      onChange={(e) =>
                        updateBlock(block.key, { newSupplierDraft: { ...block.newSupplierDraft, taxCode: e.target.value } })
                      }
                      disabled={creatingSupplier}
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={block.newSupplierDraft.email}
                      error={block.showSupplierErrors ? fieldError(block.newSupplierDraft.email, RULES.supplier.email) : undefined}
                      onChange={(e) =>
                        updateBlock(block.key, { newSupplierDraft: { ...block.newSupplierDraft, email: e.target.value } })
                      }
                      disabled={creatingSupplier}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleCreateSupplierForBlock(block.key, block.newSupplierDraft)}
                      disabled={
                        creatingSupplier ||
                        !block.newSupplierDraft.name.trim() ||
                        !block.newSupplierDraft.taxCode.trim()
                      }
                    >
                      {creatingSupplier ? "Creating…" : "Create supplier"}
                    </Button>
                  </div>
                )}

                {orderLines.length === 0 ? (
                  <div style={{ fontSize: 13, color: "var(--pc-body)" }}>
                    {itemsLoading ? "Loading items…" : "Add order lines below before quoting a supplier."}
                  </div>
                ) : (
                  <DataTable
                    columns={buildColumns(block)}
                    rows={orderLines}
                    rowKey={(l) => l.key}
                    hidePagination
                    isLoading={block.loadingPrices}
                  />
                )}
              </div>
            )
          })}

          <div>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus size={14} aria-hidden />}
              onClick={() => setBlocks((prev) => [...prev, makeBlock()])}
              disabled={blocks.length >= supplierOptions.length}
            >
              Add another supplier
            </Button>
          </div>

          <div>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={14} aria-hidden />}
              disabled={!canSave}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save proposal"}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

// Opens CreateProposalDrawer, which owns supplier search + multi-select and
// every other field (including queuing brand-new suppliers to be created
// atomically with the RFQ — see CreateProposalDrawer's newSupplierDrafts).
// Submitting is a single `createVendorProposalRequest` call carrying every
// selected/drafted supplier at once (`supplierIds` + `newSuppliers`) — the
// backend fans out to one vendor_proposal row per supplier server-side.
//
// `resolveSourceId` (optional): called right before the actual submit to get
// the real id to send. Plain usage (both source types once the record
// already exists) just returns `sourceId` as-is. The one caller that needs
// more — CreatePurchaseContainer, which can open this panel before a draft
// PO exists yet — passes a resolver that silently creates the draft PO on
// first use and returns its new id, so requesting a vendor proposal doesn't
// force the user through a separate "Create draft PO" click first.
function InviteSupplierBlock({
  sourceType,
  sourceId,
  resolveSourceId = async () => sourceId,
  orderLines,
  itemsLoading,
  supplierOptions,
  existingActiveSupplierIds,
  currentTerms,
  termsLocked,
  autoOpen,
}) {
  const [createOrderProposal, { isLoading: submittingOrder }] = useCreateVendorProposalRequestMutation()
  const [createPurchaseProposal, { isLoading: submittingPurchase }] = useCreatePurchaseVendorProposalRequestMutation()
  const createVendorProposalRequest =
    sourceType === SOURCE_TYPE.PURCHASE ? createPurchaseProposal : createOrderProposal
  const submitting = sourceType === SOURCE_TYPE.PURCHASE ? submittingPurchase : submittingOrder
  const [switchToInternalStock, { isLoading: switchingToInternal }] = useSwitchPurchaseToInternalStockMutation()
  const [drawerOpen, setDrawerOpen] = useState(autoOpen)
  const [internalOpen, setInternalOpen] = useState(false)
  const [internalSourceId, setInternalSourceId] = useState(null)

  // Internal-stock suppliers only go through "Lấy từ kho nội bộ", never an RFQ.
  const supplierDirectory = (supplierOptions || []).filter((o) => !isInternalStockSupplier(o.value)).map((o) => ({
    id: o.value,
    name: o.label,
    taxCode: o.taxCode,
    email: o.email,
  }))

  // No PO yet → create the draft first; `resolveSourceId("invite")` navigates
  // to its edit page, which reopens this drawer via `autoOpen`. Opening the
  // drawer here would lose whatever the user typed when the page is replaced.
  const handleOpenDrawer = async () => {
    if (!sourceId) {
      await resolveSourceId("invite")
      return
    }
    setDrawerOpen(true)
  }

  const handleOpenInternal = () => {
    setInternalSourceId(sourceId)
    setInternalOpen(true)
  }

  const availabilityParams = internalSourceId ? { purchaseId: internalSourceId } : null

  const handlePickWarehouse = async (warehouse) => {
    try {
      await switchToInternalStock({ id: internalSourceId, supplierId: warehouse.supplierId }).unwrap()
      showToastSuccess(`PO now ships from ${warehouse.warehouseName || "internal stock"}.`)
      setInternalOpen(false)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't switch to internal stock. Please try again."))
    }
  }

  const handleDrawerSubmit = async (payload) => {
    try {
      const id = await resolveSourceId()
      if (!id) return
      const created = await createVendorProposalRequest(
        buildCreateArgs(sourceType, id, {
          supplierIds: payload.supplierIds,
          newSuppliers: payload.newSuppliers,
          title: payload.title,
          terms: payload.requirements,
          requiredDocuments: payload.requestedDocuments.map((d) => ({
            name: d.name,
            mandatory: d.required,
          })),
          deliveryLocation: payload.deliveryLocation,
          deliveryDate: payload.expectedDeliveryDate,
          noteForVendor: payload.note,
          contactName: payload.contact.name,
          contactEmail: payload.contact.email,
          contactPhone: payload.contact.phone,
          pinEnabled: payload.requirePin,
          items: payload.lineKeys.map((k) => proposalLineRef(orderLines.find((l) => l.key === k))),
          supplierItems: payload.supplierLineKeys.map(({ supplierId, lineKeys }) => ({
            supplierId,
            items: lineKeys.map((k) => proposalLineRef(orderLines.find((l) => l.key === k))),
          })),
          deliveryFee: payload.deliveryFee,
          expiredAt: payload.deadline,
        })
      ).unwrap()
      showToastSuccess(`Đã tạo yêu cầu báo giá cho ${created.length} nhà cung cấp.`)
      warnMissingInviteEmails(created)
      setDrawerOpen(false)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể tạo yêu cầu báo giá. Vui lòng thử lại."))
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--pc-border)",
        background: "var(--pc-surface-2)",
        borderRadius: "var(--pc-r-md)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Invite suppliers to quote</div>
          <div style={{ fontSize: 13, color: "var(--pc-body)" }}>
            Pick (or create) one or more suppliers and send every request at once.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* Internal stock is procurement's call on the PO; before a PO exists it is picked
              in the create form instead (one POST /purchases with the warehouse as vendor). */}
          {sourceType === SOURCE_TYPE.PURCHASE && sourceId && (
            <Button variant="secondary" size="sm" onClick={handleOpenInternal} disabled={submitting}>
              Internal stock
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={handleOpenDrawer}>
            Create request
          </Button>
        </div>
      </div>

      <InternalStockPicker
        open={internalOpen}
        params={availabilityParams}
        busy={switchingToInternal}
        onClose={() => setInternalOpen(false)}
        onPick={handlePickWarehouse}
      />

      <CreateProposalDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleDrawerSubmit}
        suppliers={supplierDirectory}
        orderItems={orderLines}
        itemsLoading={itemsLoading}
        existingActiveSupplierIds={existingActiveSupplierIds}
        initialRequirements={currentTerms}
        requirementsLocked={termsLocked}
        isSubmitting={submitting}
      />
    </div>
  )
}

// PO edit form only: quotes already collected on the PO's linked orders/group
// (reference). The tick copies one into "Or enter prices yourself" below.
function LinkedOrderProposals({ proposals, linkedOrders, onUse }) {
  const orderCode = {}
  ;(linkedOrders || []).forEach((o) => {
    orderCode[o.id] = o.orCode || o.code
  })
  const columns = [
    {
      key: "from",
      header: "From",
      render: (p) =>
        p.orderRequestId != null
          ? `Order ${orderCode[p.orderRequestId] || `#${p.orderRequestId}`}`
          : p.groupOrderId != null
          ? `Group #${p.groupOrderId}`
          : "—",
    },
    {
      key: "supplier",
      header: "Supplier",
      render: (p) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <strong>{p.supplierName || p.supplier?.name}</strong>
          {p.isSelected && <StatusBadge status="approved" label="Selected" />}
        </span>
      ),
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      value: (p) => proposalTotal(p),
      render: (p) => (hasQuote(p) ? <Currency value={proposalTotal(p)} /> : <span className="pc-muted">{NO_QUOTE_LABEL}</span>),
    },
    {
      key: "use",
      header: "",
      align: "right",
      render: (p) => (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Check size={14} aria-hidden />}
          disabled={!hasQuote(p)}
          onClick={() => onUse(p)}
          title="Copy these prices into “Or enter prices yourself”"
        >
          Use prices
        </Button>
      ),
    },
  ]
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontWeight: 600, fontSize: 14 }}>Proposals from linked orders</div>
      <div style={{ fontSize: 13, color: "var(--pc-body)" }}>
        Reference only. Tick “Use prices” to fill the form below, then adjust and save.
      </div>
      {proposals.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--pc-muted)" }}>No proposals on the linked orders.</div>
      ) : (
        <DataTable columns={columns} rows={proposals} rowKey={(p) => p.id} hidePagination />
      )}
    </div>
  )
}

// `sourceType`: "order" (default) or "purchase" — picks which pair of
// endpoints/hooks this instance talks to (see SOURCE_TYPE/buildCreateArgs
// above). `sourceId`: the order/purchase id, when one already exists.
// `resolveSourceId` (optional async () => id): when the source might not
// exist yet at the time this panel is shown — e.g. CreatePurchaseContainer
// lets a user start requesting vendor quotes before the draft PO itself has
// been created — pass a resolver that creates it on first use and returns
// its id. Without a resolver, a missing `sourceId` just blocks (the
// long-standing OR behavior: save the order as a draft first).
export default function VendorProposalsPanel({
  sourceType = SOURCE_TYPE.ORDER,
  sourceId,
  resolveSourceId,
  orderLines,
  orderProposals,
  linkedOrders,
  itemsLoading,
  supplierOptions,
  onCreateSupplier,
  creatingSupplier,
  autoOpen,
  flushRef,
}) {
  const [prefill, setPrefill] = useState(null)
  const orderProposalsQ = useGetVendorProposalsQuery(sourceId, {
    skip: sourceType !== SOURCE_TYPE.ORDER || !sourceId,
  })
  const purchaseProposalsQ = useGetPurchaseVendorProposalsQuery(sourceId, {
    skip: sourceType !== SOURCE_TYPE.PURCHASE || !sourceId,
  })
  const groupProposalsQ = useGetGroupVendorProposalsQuery(sourceId, {
    skip: sourceType !== SOURCE_TYPE.GROUP || !sourceId,
  })
  const { data: proposals, isLoading } =
    sourceType === SOURCE_TYPE.PURCHASE
      ? purchaseProposalsQ
      : sourceType === SOURCE_TYPE.GROUP
      ? groupProposalsQ
      : orderProposalsQ
  // OR/PO detail carries `proposalRequirements` { locked, criteria[] } (none on group).
  const orderDetailQ = useGetOrderReviewQuery(sourceId, { skip: sourceType !== SOURCE_TYPE.ORDER || !sourceId })
  const purchaseDetailQ = useGetPurchaseQuery(sourceId, { skip: sourceType !== SOURCE_TYPE.PURCHASE || !sourceId })
  const proposalRequirements = (sourceType === SOURCE_TYPE.PURCHASE ? purchaseDetailQ : orderDetailQ).data
    ?.proposalRequirements
  const currentTerms = requirementNames(proposalRequirements?.criteria)
  const termsLocked = !!proposalRequirements?.locked
  const [compareOpen, setCompareOpen] = useState(false)
  const hasQuotes = (proposals || []).some(hasQuote)

  if (!sourceId && !resolveSourceId) {
    return (
      <div className="pc-section-empty">
        <div className="pc-section-empty-title">No vendor proposals yet</div>
        Save this order as a draft first, then come back to add vendor proposals.
      </div>
    )
  }

  // Suppliers with a still-open (non-expired) request already — disabled in
  // the drawer's search so staff don't send a second one on top.
  const existingActiveSupplierIds = new Set(
    (proposals || []).filter((p) => !isExpired(p)).map((p) => String(p.supplierId))
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {sourceId && sourceType !== SOURCE_TYPE.GROUP && (
        <RequirementsSection sourceType={sourceType} sourceId={sourceId} terms={currentTerms} locked={termsLocked} />
      )}
      {sourceId && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasQuotes}
              onClick={() => setCompareOpen(true)}
              title={hasQuotes ? undefined : "Waiting for at least one vendor to submit a quotation."}
            >
              Review and compare quotation
            </Button>
          </div>
          <ProposalsTable proposals={proposals} isLoading={isLoading} />
          <VendorComparisonModal
            open={compareOpen}
            onClose={() => setCompareOpen(false)}
            proposals={proposals}
            sourceType={sourceType}
            sourceId={sourceId}
          />
        </>
      )}
      <InviteSupplierBlock
        sourceType={sourceType}
        sourceId={sourceId}
        resolveSourceId={resolveSourceId}
        orderLines={orderLines}
        itemsLoading={itemsLoading}
        supplierOptions={supplierOptions}
        existingActiveSupplierIds={existingActiveSupplierIds}
        currentTerms={currentTerms}
        termsLocked={termsLocked}
        autoOpen={autoOpen === "invite"}
      />
      {orderProposals && (
        <LinkedOrderProposals
          proposals={orderProposals}
          linkedOrders={linkedOrders}
          onUse={(proposal) => setPrefill((prev) => ({ seq: (prev?.seq || 0) + 1, proposal }))}
        />
      )}
      <NewProposalForm
        prefill={prefill}
        sourceType={sourceType}
        sourceId={sourceId}
        resolveSourceId={resolveSourceId}
        orderLines={orderLines}
        orderProposals={orderProposals}
        currentTerms={currentTerms}
        itemsLoading={itemsLoading}
        supplierOptions={supplierOptions}
        onCreateSupplier={onCreateSupplier}
        creatingSupplier={creatingSupplier}
        autoOpen={autoOpen === "prices"}
        flushRef={flushRef}
      />
    </div>
  )
}
